/**
 * Theme-preference hook + context. The provider (ThemeModeProvider) lives in
 * `src/components/ThemeModeProvider.tsx`; outside it, `useThemeMode`
 * degrades to a fixed "system" preference.
 *
 * The preference has three values — light / dark / system — persisted in
 * localStorage; "system" follows prefers-color-scheme live.
 */
import { createContext, useContext } from "react";
import { LS_THEME_KEY } from "../lib/config";

export type ThemePref = "light" | "dark" | "system";

export const THEME_PREFS: ThemePref[] = ["light", "dark", "system"];

export function isThemePref(value: string): value is ThemePref {
  return (THEME_PREFS as string[]).includes(value);
}

export function loadThemePref(): ThemePref {
  try {
    const stored = window.localStorage.getItem(LS_THEME_KEY);
    return stored && isThemePref(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function saveThemePref(pref: ThemePref): void {
  try {
    window.localStorage.setItem(LS_THEME_KEY, pref);
  } catch {
    /* private mode */
  }
}

export type ThemeModeValue = {
  pref: ThemePref;
  setPref: (pref: ThemePref) => void;
};

export const ThemeModeContext = createContext<ThemeModeValue>({
  pref: "system",
  setPref: () => undefined,
});

export function useThemeMode(): ThemeModeValue {
  return useContext(ThemeModeContext);
}
